import loginScreenImageUrl from "@/assets/login-screen.svg"
import BackButton from "@/components/BackButton"
import { Button } from "@/components/chadcn/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/chadcn/Dialog"
import { Label } from "@/components/chadcn/Label"
import HeroImage from "@/components/HeroImage"
import { EmailInput, PhoneInput, TextInput } from "@/components/InputFields"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useCreateContactRequestMutation } from "@/data/queries"
import { contactRequestSchema, type ContactRequestFormData } from "@/schemas/static"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useLocation } from "react-router"

type LocationState = { showBackButton?: boolean } | null

const ContactBloomlifePage = () => {
  const location = useLocation()
  const showBackButton = (location.state as LocationState)?.showBackButton ?? true

  const [submitted, setSubmitted] = useState(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)

  const { mutateAsync: createContactRequest, isPending } = useCreateContactRequestMutation()
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ContactRequestFormData>({
    mode: "all",
    defaultValues: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
    },
    resolver: zodResolver(contactRequestSchema),
  })

  const onSubmit = async (formData: ContactRequestFormData) => {
    try {
      await createContactRequest(formData)
      setSubmitted(true)
    } catch {
      setErrorDialogOpen(true)
    }
  }

  return (
    <Page showAppbar={false}>
      {showBackButton && !submitted && (
        <div className="absolute left-4 top-4 z-10">
          <BackButton />
        </div>
      )}
      <HeroImage src={loginScreenImageUrl} />
      <div className="flex flex-col gap-6">
        {submitted ? (
          <div className="flex flex-col gap-3">
            <h1 className="text-h2">Thank you!</h1>
            <p className="text-base text-primary-contrast/80">
              We&apos;ve received your information and will reach out to you within 1 business day.
            </p>
            <p className="text-base text-primary-contrast/80">
              Please note that you won&apos;t be able to use the device until we contact you
              directly.
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-h2">
              Contact Bloomlife for support by email or text to create an account.
            </h1>

            <form className="flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-col gap-1">
                <Label htmlFor="firstName">First Name *</Label>
                <TextInput
                  id="firstName"
                  disabled={isPending}
                  {...register("firstName")}
                  isError={!!errors.firstName?.message}
                />
                {errors.firstName?.message && (
                  <p className="text-sm text-error">{errors.firstName.message}</p>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="lastName">Last Name *</Label>
                <TextInput
                  id="lastName"
                  disabled={isPending}
                  {...register("lastName")}
                  isError={!!errors.lastName?.message}
                />
                {errors.lastName?.message && (
                  <p className="text-sm text-error">{errors.lastName.message}</p>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="phoneNumber">Phone number *</Label>
                <PhoneInput
                  id="phoneNumber"
                  disabled={isPending}
                  {...register("phoneNumber")}
                  placeholder="+13105551234"
                  isError={!!errors.phoneNumber?.message}
                />
                {errors.phoneNumber?.message && (
                  <p className="text-sm text-error">{errors.phoneNumber.message}</p>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="email">Email *</Label>
                <EmailInput
                  id="email"
                  disabled={isPending}
                  {...register("email")}
                  placeholder="email@example.com"
                  isError={!!errors.email?.message}
                />
                {errors.email?.message && (
                  <p className="text-sm text-error">{errors.email.message}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isPending || !isValid}
                className="flex w-full gap-3 py-2 text-base"
              >
                {isPending && <Spinner />}
                {isPending ? "Submitting..." : "Submit"}
              </Button>
            </form>
          </>
        )}
      </div>

      <Dialog open={errorDialogOpen} onOpenChange={setErrorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submission Failed</DialogTitle>
            <DialogDescription>
              We&apos;re sorry, but we couldn&apos;t submit your contact request. Please try again
              or contact us directly.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" onClick={() => setErrorDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  )
}

export default ContactBloomlifePage
